import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { ProductResponse } from "@/app/api/types/product";
import type { CustomEntity } from "@/app/api/types/preOrder";
import { useDebounce } from "@/hooks/use-debounce";
import { useDropdownState } from "@/hooks/use-dropdown-state";
import { UUID } from "crypto";
import { focusNextTabCell } from "../../utils/tabNavigation";
import type { StockSummary } from "@/query/hooks/useGlobalStocks";
import { buildStockMapFromItems } from "@/query/hooks/useGlobalStocks";
import type { CustomFieldDefinition } from "@/app/api/types/featureFlags";
import { useProductSelectData } from "./useProductSelectData";
import { useProductSelectKeyboard } from "./useProductSelectKeyboard";
import { computeDropdownPlacement } from "./dropdown-hooks";
import {
  resolveInitialStockFilter,
  type StockFilterMode,
} from "./stockFilterMode";

export const PRODUCT_COMBO_FILTER_IN_STOCK_KEY = "productCombo_filterInStock";
export const PRODUCT_COMBO_FILTER_PRICED_KEY = "productCombo_filterPriced";

export interface ProductSelectComboProps {
  value: UUID | null;
  onChange: (
    productId: UUID | null,
    subproductId?: UUID | null,
    product?: ProductResponse | null,
  ) => void;
  placeholder?: string;
  /**
   * Seed text for the dropdown search input, used only when no product is
   * matched yet (no `value`/`product`). Lets a caller pre-fill the search
   * with the term the row is "asking for" — e.g. a price proposal whose
   * AI-read reference hasn't been matched to a product — so opening the
   * picker immediately filters to likely matches. Ignored once a product is
   * selected (the matched product's own name seeds instead).
   */
  searchSeed?: string;
  className?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  isLastRow?: boolean;
  product?: ProductResponse | null;
  portalContainer?: HTMLElement | null;
  priceLookup?: (productId: string) => number | null;
  /**
   * Hide the inline price chip in the CLOSED product display while still
   * passing `priceLookup` through to the open dropdown items. Recap rows
   * set this true since the dedicated PriceCell already shows the same
   * (TARIF/LISTE) preview right next to the product — the chip was
   * rendering it a second time.
   */
  hideClosedPricePill?: boolean;
  stockLookup?: (productId: string) => StockSummary | null;
  stockCustomFields?: CustomFieldDefinition[];
  stockLocationLabel?: string | null;
  stockLocations?: Array<{ id: string; name: string; code: string | null }>;
  stockLocationId?: string | null;
  rawStocks?: import("@/app/api/types/stock").StockRecapSummaryItem[];
  showProductMetadata?: boolean;
  customProduct?: CustomEntity | null;
  onCustomProductChange?: (cp: CustomEntity | null) => void;
  allowCustom?: boolean;
  /**
   * Pre-computed picker corpus from a caller that already holds the catalog
   * (recap's `ProductLookupsContext`). When set, the combo takes no catalog
   * subscription of its own. Must be non-variant + awake — see
   * `selectNonVariantProducts` in `FlowsTable.tsx`. Mirrors
   * `EntitySelectWithTags`'s `allEntities` override (STK-1863).
   */
  allProductsOverride?: ProductResponse[];
}

export function useProductSelectState(
  props: ProductSelectComboProps,
  stockFilterMode: StockFilterMode = "persist",
) {
  const {
    value,
    onChange,
    disabled = false,
    isLastRow = false,
    product: preloadedProduct,
    priceLookup,
    stockLookup,
    stockCustomFields,
    stockLocations,
    stockLocationId,
    rawStocks,
    customProduct,
    allowCustom = false,
    searchSeed,
    allProductsOverride,
  } = props;

  // ── Internal stock/location state ──────────────────────────────────
  // The combo owns its location state. Pre-order sets the initial value
  // and can update it, but the dropdown can also change it independently.
  // All stock filtering/display inside the dropdown uses this.
  const [localLocationId, setLocalLocationId] = useState<string | null>(
    stockLocationId ?? null,
  );
  // Sync prop changes to local state via effect (not during render)
  useEffect(() => {
    setLocalLocationId(stockLocationId ?? null);
  }, [stockLocationId]);

  // Stock lookup built from rawStocks + current location — drives dropdown
  const internalStockLookup = useMemo<
    ((productId: string) => StockSummary | null) | undefined
  >(() => {
    if (!rawStocks) return stockLookup; // no raw data → fall back to prop
    const filtered = localLocationId
      ? rawStocks.filter((s) => s.location_id === localLocationId)
      : rawStocks;
    const map = buildStockMapFromItems(filtered);
    return (productId: string) => map.get(productId) ?? null;
  }, [rawStocks, stockLookup, localLocationId]);

  // UI state
  const [isOpen, setIsOpen] = useState(false);
  const [customDialogOpen, setCustomDialogOpen] = useState(false);
  const isCustomActive = !!(allowCustom && customProduct?.name && !value);
  // Seed from `preloadedProduct` so the shell→editor hydration swap is
  // byte-identical on the first render. Without this, the editor's first
  // paint would show the placeholder text for one frame (the useEffect in
  // `useProductSelectData` only sets this post-mount), producing a visible
  // flicker on every hydrated cell.
  const [currentProductDetails, setCurrentProductDetails] =
    useState<ProductResponse | null>(
      preloadedProduct && preloadedProduct.id === value
        ? preloadedProduct
        : null,
    );
  const [searchTerm, setSearchTerm] = useState("");
  // SKU input also seeded so the editor's first paint matches the shell.
  const [skuInput, setSkuInput] = useState(
    preloadedProduct && preloadedProduct.id === value
      ? (preloadedProduct.code ?? "")
      : "",
  );
  const [previousSkuValue, setPreviousSkuValue] = useState("");
  const [selectedDropdownIndex, setSelectedDropdownIndex] = useState(-1);
  const [openedByButton, setOpenedByButton] = useState(false);
  const [hasInitializedInput, setHasInitializedInput] = useState(false);
  const [isManuallyNavigating, setIsManuallyNavigating] = useState(false);
  const [filterInStock, setFilterInStock] = useState(false);
  const [filterPriced, setFilterPriced] = useState(false);
  // When opening with a seeded product name, we want the dropdown to paint
  // first (full unfiltered list) and apply the seed filter on the next frame —
  // so click→painted is bounded by render cost, not filter cost. Flips to
  // true after rAF×2 in handleToggle. Resets when the dropdown closes.
  const [seedAppliedToFilter, setSeedAppliedToFilter] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    width: number;
    maxHeight: number;
  }>({ left: 0, width: 0, maxHeight: 480 });

  const [hoveredProductId, setHoveredProductId] = useState<UUID | null>(null);
  const [submenuPosition, setSubmenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  // Refs
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const skuInputRef = useRef<HTMLInputElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const observerTarget = useRef<HTMLDivElement>(null);
  // Tracks the search term seeded on dropdown open. While searchTerm still
  // equals this seed, we bypass the 150ms debounce so the first paint is
  // already filtered (no flicker). Once the user types and the term diverges,
  // debouncing resumes for the rest of the session.
  const searchSeedRef = useRef<string | null>(null);

  // Dropdown persistence
  const {
    state: dropdownState,
    updateSelectedValue,
    updateSearchQuery,
    updateExpanded,
  } = useDropdownState("product_select_combo");

  // Short debounce: in-flight searches are cancellable (time-sliced index +
  // AbortController in useProductSelectData), so its only job is to skip
  // searches for keystrokes mid-word, not to protect the main thread.
  const debouncedSearchTerm = useDebounce(searchTerm, 80);
  // SKU input uses O(1) prefix index — no debounce needed
  const isUntouchedSeed =
    searchSeedRef.current !== null && searchTerm === searchSeedRef.current;
  // Three regimes for the filter input:
  //  1. Untouched seed, before rAF×2 fires → "" so the open frame is a cheap
  //     passthrough render of the full list.
  //  2. Untouched seed, after rAF×2 → raw seed (no debounce); filter runs on
  //     the next render so alternatives appear as fast as physically possible.
  //  3. User has typed → debounced as usual.
  const buttonSearchTerm = (() => {
    if (!isUntouchedSeed) return debouncedSearchTerm;
    return seedAppliedToFilter ? searchTerm : "";
  })();
  const effectiveSearchTerm = openedByButton ? buttonSearchTerm : skuInput;

  // Sync dropdown state to localStorage
  useEffect(() => {
    if (dropdownState.searchQuery !== searchTerm) {
      updateSearchQuery(searchTerm);
    }
  }, [searchTerm, dropdownState.searchQuery, updateSearchQuery]);

  useEffect(() => {
    if (dropdownState.isExpanded !== isOpen) {
      updateExpanded(isOpen);
    }
  }, [isOpen, dropdownState.isExpanded, updateExpanded]);

  // Data layer (fetching, filtering, pagination)
  const data = useProductSelectData({
    value,
    preloadedProduct,
    priceLookup,
    stockLookup: internalStockLookup,
    stockCustomFields,
    effectiveSearchTerm,
    rawSearchTerm: searchTerm,
    openedByButton,
    filterInStock,
    filterPriced,
    isOpen,
    isManuallyNavigating,
    selectedDropdownIndex,
    setSelectedDropdownIndex,
    setCurrentProductDetails,
    setSkuInput,
    setPreviousSkuValue,
    setIsOpen,
    setSearchTerm,
    hasInitializedInput,
    setHasInitializedInput,
    observerTarget,
    isUntouchedSeed,
    allProductsOverride,
  });

  // Side effects for dropdown behavior
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !buttonRef.current?.contains(event.target as Node) &&
        !skuInputRef.current?.contains(event.target as Node)
      ) {
        searchSeedRef.current = null;
        setIsOpen(false);
        setOpenedByButton(false);
        setSearchTerm("");
        setSeedAppliedToFilter(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  useEffect(() => {
    function handleScrollOrResize() {
      if (isOpen) {
        updateDropdownPosition();
      }
    }

    if (isOpen) {
      handleScrollOrResize();
      window.addEventListener("scroll", handleScrollOrResize, true);
      window.addEventListener("resize", handleScrollOrResize);

      const observer = new MutationObserver(() => {
        requestAnimationFrame(handleScrollOrResize);
      });
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class", "style"],
      });

      return () => {
        window.removeEventListener("scroll", handleScrollOrResize, true);
        window.removeEventListener("resize", handleScrollOrResize);
        observer.disconnect();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- updateDropdownPosition is defined below, stable via useCallback
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && openedByButton) {
      requestAnimationFrame(() => {
        const el = searchInputRef.current;
        if (!el) return;
        // Keyboard open: the typeahead handler in useProductSelectKeyboard
        // has already focused the search input and placed the cursor at
        // the end of the typed character. Running `select()` here would
        // re-select the user's typed letter, so the next keystroke
        // replaces it — they'd lose every character but the last.
        // Click open: focus is still on the button — focus + select so
        // the user can immediately overwrite the seeded product name.
        if (document.activeElement === el) return;
        el.focus();
        el.select();
      });
    }
  }, [isOpen, openedByButton]);

  // Position calculation
  const updateDropdownPosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownHeight = 480;
      const enrichedWidth =
        500 +
        (internalStockLookup ? 64 + data.dropdownColumns.length * 40 : 0) +
        (priceLookup ? 64 : 0);
      const dropdownWidth = data.isEnriched
        ? Math.max(enrichedWidth, rect.width)
        : Math.max(320, rect.width);

      const placement = computeDropdownPlacement(
        rect,
        dropdownHeight,
        dropdownWidth,
        { width: window.innerWidth, height: window.innerHeight },
      );
      setDropdownPosition(placement);
    }
  }, [
    internalStockLookup,
    priceLookup,
    data.isEnriched,
    data.dropdownColumns.length,
  ]);

  // Product selection
  const handleProductSelect = useCallback(
    (
      product: ProductResponse,
      subproductId?: UUID | null,
      { skipAutoAdvance = false } = {},
    ) => {
      onChange(product.id, subproductId, product);
      updateSelectedValue(product.id);
      searchSeedRef.current = null;
      setIsOpen(false);
      setSearchTerm("");
      setOpenedByButton(false);
      setSelectedDropdownIndex(-1);
      setHoveredProductId(null);
      setSubmenuPosition(null);
      setSeedAppliedToFilter(false);

      // For the empty "add product" row, keep display reset so it shows
      // the placeholder immediately instead of flashing the selected product name.
      if (isLastRow) {
        setCurrentProductDetails(null);
        setSkuInput("");
        setPreviousSkuValue("");
        setHasInitializedInput(false);
      } else {
        const sku = product.sku || "";
        setSkuInput(sku);
        setPreviousSkuValue(sku);
        setCurrentProductDetails(product);
        setHasInitializedInput(true);
      }

      if (skipAutoAdvance) return;

      // For existing rows (product change), advance to next tab cell.
      // For the EmptyProductRow (isLastRow), FlowsTable's useLayoutEffect
      // auto-focuses the new row's quantity cell after optimistic render.
      if (!isLastRow) {
        requestAnimationFrame(() => {
          if (skuInputRef.current) {
            focusNextTabCell(skuInputRef.current);
          }
        });
      }
    },
    [onChange, updateSelectedValue, isLastRow],
  );

  const handleSubproductSelect = useCallback(
    (product: ProductResponse, subproductId: UUID) => {
      handleProductSelect(product, subproductId);
    },
    [handleProductSelect],
  );

  const handleToggle = useCallback(() => {
    if (disabled) return;

    if (!isOpen) {
      updateDropdownPosition();
      setFilterInStock(
        resolveInitialStockFilter(
          stockFilterMode,
          localStorage.getItem(PRODUCT_COMBO_FILTER_IN_STOCK_KEY) !== "false",
        ),
      );
      setFilterPriced(
        localStorage.getItem(PRODUCT_COMBO_FILTER_PRICED_KEY) === "true",
      );
      const seed =
        currentProductDetails?.short_name ||
        currentProductDetails?.name ||
        searchSeed ||
        "";
      searchSeedRef.current = seed;
      setSearchTerm(seed);
      setSeedAppliedToFilter(false);
      setIsOpen(true);
      setOpenedByButton(true);
      // After paint 1 (full list, passthrough), flip the filter input to the
      // seed so paint 2 shows the filtered alternatives. rAF×2 ensures we run
      // strictly after the open frame has been committed and painted.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setSeedAppliedToFilter(true);
        });
      });
    } else {
      searchSeedRef.current = null;
      setIsOpen(false);
      setOpenedByButton(false);
      setSearchTerm("");
      setSeedAppliedToFilter(false);
      setSelectedDropdownIndex(-1);
      data.setRenderPage(() => 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- data.setRenderPage is stable
  }, [
    disabled,
    isOpen,
    updateDropdownPosition,
    currentProductDetails,
    searchSeed,
    stockFilterMode,
  ]);

  // Keyboard handlers (extracted to separate hook)
  const keyboard = useProductSelectKeyboard({
    isOpen,
    disabled,
    isLastRow,
    selectedDropdownIndex,
    previousSkuValue,
    productsToRender: data.productsToRender,
    isSearchPending: data.isSearchPending,
    stockFilterMode,
    setIsOpen,
    setSearchTerm,
    setOpenedByButton,
    setSelectedDropdownIndex,
    setIsManuallyNavigating,
    setFilterInStock,
    setFilterPriced,
    setSkuInput,
    buttonRef,
    dropdownRef,
    searchInputRef,
    handleProductSelect,
    handleToggle,
    updateDropdownPosition,
  });

  // SKU input handlers
  const handleSkuChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value.slice(0, 20);
      setSkuInput(val);
      setOpenedByButton(false);
      setIsManuallyNavigating(false);

      if (!isOpen && val.trim()) {
        updateDropdownPosition();
        setFilterInStock(
          resolveInitialStockFilter(
            stockFilterMode,
            localStorage.getItem(PRODUCT_COMBO_FILTER_IN_STOCK_KEY) !== "false",
          ),
        );
        setFilterPriced(
          localStorage.getItem(PRODUCT_COMBO_FILTER_PRICED_KEY) === "true",
        );
        setIsOpen(true);
      }

      if (isOpen && !val.trim() && !openedByButton) {
        setIsOpen(false);
      }
    },
    [isOpen, openedByButton, updateDropdownPosition, stockFilterMode],
  );

  const handleSkuBlur = useCallback(() => {
    setSkuInput(previousSkuValue);
    if (isOpen && !openedByButton) {
      setIsOpen(false);
    }
  }, [previousSkuValue, isOpen, openedByButton]);

  const handleSkuFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      e.currentTarget.select();
      setPreviousSkuValue(skuInput);
    },
    [skuInput],
  );

  return {
    // State
    isOpen,
    setIsOpen,
    customDialogOpen,
    setCustomDialogOpen,
    isCustomActive,
    currentProductDetails,
    setCurrentProductDetails,
    searchTerm,
    setSearchTerm,
    effectiveSearchTerm,
    skuInput,
    setSkuInput,
    previousSkuValue,
    setPreviousSkuValue,
    selectedDropdownIndex,
    setSelectedDropdownIndex,
    openedByButton,
    setOpenedByButton,
    isManuallyNavigating,
    setIsManuallyNavigating,
    filterInStock,
    setFilterInStock,
    filterPriced,
    setFilterPriced,
    dropdownPosition,
    hoveredProductId,
    setHoveredProductId,
    submenuPosition,
    setSubmenuPosition,
    localLocationId,
    setLocalLocationId,
    internalStockLookup,
    renderPage: data.renderPage,
    setRenderPage: data.setRenderPage,

    // Refs
    buttonRef,
    dropdownRef,
    skuInputRef,
    searchInputRef,
    observerTarget,

    // Data (from useProductSelectData)
    allProducts: data.allProducts,
    allProductsWithChildren: data.allProductsWithChildren,
    productsToRender: data.productsToRender,
    filteredProducts: data.filteredProducts,
    filterCounts: data.filterCounts,
    isSearchPending: data.isSearchPending,
    productsLoading: data.productsLoading,
    hasMoreRenderPages: data.hasMoreRenderPages,
    dropdownColumns: data.dropdownColumns,
    isEnriched: data.isEnriched,
    stockSummary: data.stockSummary,
    getProductChildren: data.getProductChildren,

    // Handlers
    handleToggle,
    handleKeyDown: keyboard.handleKeyDown,
    handleButtonKeyDown: keyboard.handleButtonKeyDown,
    handleProductSelect,
    handleSubproductSelect,
    scrollSelectedItemIntoView: keyboard.scrollSelectedItemIntoView,
    updateDropdownPosition,
    handleSkuChange,
    handleSkuBlur,
    handleSkuFocus,
  };
}
